// src/pages/health/HealthCare.jsx
import { useMemo, useState } from "react";
import { addMonths, subMonths, startOfMonth, format, isSameDay } from "date-fns";
import { ko } from "date-fns/locale";
import Calendar from "react-calendar";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

import { useNavigate, useParams } from "react-router-dom";
import AuthCard from "../../components/auth/AuthCard";
import BottomNav from "../../components/common/BottomNav";
import { useLoadingGate } from "../../hooks/useLoadingGate";

// hooks
import {
    useBpMonth, useBpDay, useCreateBp,
    useWeightMonth, useWeightDay, useCreateWeight,
    useBodyFatMonth, useBodyFatDay, useCreateBodyFat,
    useGlucoseMonth, useGlucoseDay, useCreateGlucose
} from "../../hooks/useHealthMetrics";

import HealthCareModal from "./components/healthCareModal";

const getBodyFatMass = (r) =>
    r?.body_fat ?? r?.body_fat_mass ?? r?.bodyFatMass ?? r?.fat_mass ?? r?.bodyFat ?? r?.value;

// 메타 - 타입별 라벨/컬러/훅/필드
const METAS = {
    bp: {
        label: "혈압 기록",
        color: "#ED6363",
        monthHook: useBpMonth,
        dayHook: useBpDay,
        fields: { primary: "sys", secondary: "dia" },
        formatMain: (r) => `${r.sys} / ${r.dia}`,
        chartLines: [
            { key: "sys", color: "#ED6363", name: "수축" },
            { key: "dia", color: "#5F9BFF", name: "이완" },
        ],
        canCreate: true,
        modalVariant: "bp",
    },
    weight: {
        label: "체중 기록",
        color: "#7EDBB6",
        monthHook: useWeightMonth,
        dayHook: useWeightDay,
        fields: { primary: "weight" },
        formatMain: (r) => `${r.weight} kg`,
        chartLines: [{ key: "weight", color: "#7EDBB6", name: "체중" }],
        canCreate: true,            // ✅ 체중도 추가 허용
        modalVariant: "weight",     // ✅ 모달을 체중 입력으로
    },
    "body-fat": {
        label: "체지방량 기록",
        color: "#B48CF2",
        monthHook: useBodyFatMonth,
        dayHook: useBodyFatDay,
        fields: { primary: "body_fat" },
        formatMain: (r) => `${getBodyFatMass(r) ?? "-"} kg`,
        chartLines: [
            { key: "body_fat", color: "#B48CF2", name: "체지방량", getValue: getBodyFatMass },
        ],
        canCreate: true,
        modalVariant: "bodyFat",
    },
    glucose: {
        label: "혈당 기록",
        color: "#E9D045",
        monthHook: useGlucoseMonth,
        dayHook: useGlucoseDay,
        fields: { primary: "fasting" },
        formatMain: (r) => `${r.fasting} mg/dL`,
        chartLines: [{ key: "fasting", color: "#E9D045", name: "공복혈당" }],
        canCreate: true,           // (원하면 추후 확대)
        modalVariant: "glucose",
    },
};

export default function HealthCare() {
    const ready = useLoadingGate();
    const navigate = useNavigate();
    const { metric } = useParams(); // /health/:metric
    const key = metric ?? "bp";
    const meta = METAS[key] ?? METAS.bp;

    // 보기 모드
    const [view, setView] = useState("calendar");

    // 캘린더 기준 월/선택일
    const [selected, setSelected] = useState(new Date());
    const [month, setMonth] = useState(startOfMonth(new Date()));
    const goPrev = () => setMonth((m) => startOfMonth(subMonths(m, 1)));
    const goNext = () => setMonth((m) => startOfMonth(addMonths(m, 1)));

    // 데이터 로딩(타입별 훅 선택)
    const { data: monthData = [] } = meta.monthHook(month);
    const { data: dayData = [] } = meta.dayHook(selected);

    /** 달력 점 표시용 카운트 */
    const countMap = useMemo(() => {
        const m = new Map();
        for (const r of monthData) {
            const k = format(new Date(r.dt), "yyyy-MM-dd");
            m.set(k, (m.get(k) ?? 0) + 1);
        }
        return m;
    }, [monthData]);

    /** 선택된 날짜의 기록(시간 내림차순) */
    const dayReadings = useMemo(() => {
        return [...dayData].sort((a, b) => new Date(b.dt) - new Date(a.dt));
    }, [dayData]);

    /** 차트 데이터: 날짜별 가장 늦은 기록 1개 */
    const chartData = useMemo(() => {
        const perDay = new Map(); // dd -> record
        monthData.forEach((r) => {
            const d = new Date(r.dt);
            const dd = format(d, "dd");
            const prev = perDay.get(dd);
            if (!prev || new Date(prev.dt) < d) perDay.set(dd, r);
        });
        return [...perDay.entries()]
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([dd, r]) => {
                const base = { label: dd };
                meta.chartLines.forEach(({ key, getValue }) => {
                    base[key] = getValue ? getValue(r) : r[key];
                });
                return base;
            });
    }, [monthData, meta.chartLines]);

    /* ─────────── 모달(추가: bp/weight 지원) ─────────── */
    const [openAdd, setOpenAdd] = useState(false);

    // metric별 생성 훅
    const bpCreate = useCreateBp();
    const weightCreate = useCreateWeight();
    const bodyFatCreate = useCreateBodyFat();
    const glucoseCreate = useCreateGlucose();

    const createMap = {
        bp: bpCreate,
        weight: weightCreate,
        "body-fat": bodyFatCreate,
        glucose: glucoseCreate,
    };
    const currentCreate = createMap[key]; // undefined면 생성 미지원
    const isPending = currentCreate?.isPending ?? false;

    const handleOpenAdd = () => setOpenAdd(true);
    const handleCloseAdd = () => setOpenAdd(false);

    // 모달에서 넘어온 값 저장
    const handleSubmitAdd = (payload) => {
        if (!currentCreate) return; // 생성 미지원
        currentCreate.mutate(payload, {
            onSuccess: () => setOpenAdd(false),
        });
    };

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7 bg-white">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard className="flex-1 w-full mx-auto" title={meta.label} onClose={() => navigate(-1)}>
                    <div className="flex flex-col flex-1 pt-3 pb-24">
                        {/* 월 이동/표시 */}
                        <div className="w-full flex items-center justify-between pb-7">
                            <button type="button" onClick={goPrev} className="rounded-md active:scale-95" aria-label="이전 달">
                                <ChevronLeftIcon className="w-6 h-6 text-gray-400" />
                            </button>
                            <div className="font-extrabold text-center text-2xl md:text-3xl">
                                {format(month, "yyyy년 M월", { locale: ko })}
                            </div>
                            <button type="button" onClick={goNext} className="rounded-md active:scale-95" aria-label="다음 달">
                                <ChevronRightIcon className="w-6 h-6 text-gray-400" />
                            </button>
                        </div>

                        {/* 캘린더 or 차트 */}
                        {view === "calendar" ? (
                            <div className="w-full flex items-center justify-center sc-cal px-1.5">
                                <Calendar
                                    activeStartDate={month}
                                    onActiveStartDateChange={({ activeStartDate }) => setMonth(startOfMonth(activeStartDate))}
                                    view="month"
                                    formatShortWeekday={(locale, date) => format(date, "EEE", { locale: ko })}
                                    value={selected}
                                    onClickDay={(d) => setSelected(d)}
                                    formatDay={() => ""}
                                    showNavigation={false}
                                    locale="ko-KR"
                                    calendarType="gregory"
                                    tileClassName={({ date }) => {
                                        const base =
                                            "!flex !flex-col !items-center !justify-center !py-1 !min-h-[56px] md:!py-3 md:!min-h-[64px] focus:outline-none focus-visible:outline-none";
                                        return isSameDay(date, selected)
                                            ? `${base} !bg-white !text-black ring-2 rounded-xl`
                                            : base;
                                    }}
                                    tileContent={({ date, view }) => {
                                        if (view !== "month") return null;
                                        const key = format(date, "yyyy-MM-dd");
                                        const cnt = countMap.get(key) ?? 0;
                                        return (
                                            <div className="pointer-events-none leading-none flex flex-col items-center font-semibold text-sm md:text-base">
                                                <div className="text-lg md:text-xl">{format(date, "d")}</div>
                                                <div
                                                    className={`mt-0.5 md:mt-1 w-2 h-2 rounded-full ${cnt > 0 ? "" : "opacity-0"}`}
                                                    style={{ backgroundColor: meta.color }}
                                                />
                                            </div>
                                        );
                                    }}
                                />
                            </div>
                        ) : (
                            <section className="px-1.5">
                                <div className="rounded-2xl border border-gray-200 p-3 md:p-4">
                                    <div className="h-56 md:h-72">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
                                                <YAxis
                                                    tick={{ fontSize: 12, fill: "#9CA3AF" }}
                                                    width={28}
                                                    axisLine={false}
                                                    tickLine={false}
                                                    domain={["dataMin-10", "dataMax+10"]}
                                                />
                                                <Tooltip formatter={(v, name) => [v, name]} labelFormatter={(l) => `${format(month, "M")}월 ${l}일`} />
                                                {meta.chartLines.map((ln) => (
                                                    <Line
                                                        key={ln.key}
                                                        type="monotone"
                                                        dataKey={ln.key}
                                                        stroke={ln.color}
                                                        strokeWidth={2.5}
                                                        dot={{ r: 4 }}
                                                        activeDot={{ r: 5 }}
                                                        name={ln.name}
                                                    />
                                                ))}
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </section>
                        )}

                        {/* 보기 전환 버튼 */}
                        <div className="mt-5 grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setView("calendar")}
                                className={`h-11 md:h-12 rounded-xl font-semibold border ${view === "calendar" ? "text-white" : "bg-white"}`}
                                style={{
                                    backgroundColor: view === "calendar" ? meta.color : "white",
                                    borderColor: meta.color,
                                    color: view === "calendar" ? "white" : meta.color,
                                }}
                            >
                                달력으로 보기
                            </button>
                            <button
                                type="button"
                                onClick={() => setView("chart")}
                                className={`h-11 md:h-12 rounded-xl font-semibold border ${view === "chart" ? "text-white" : "bg-white"}`}
                                style={{
                                    backgroundColor: view === "chart" ? meta.color : "white",
                                    borderColor: meta.color,
                                    color: view === "chart" ? "white" : meta.color,
                                }}
                            >
                                그래프로 보기
                            </button>
                        </div>

                        {dayReadings.length > 0 && (
                            <section className="mt-4 space-y-3">
                                {dayReadings.map((r, idx) => (
                                    <button
                                        key={`${r.dt}-${idx}`}
                                        type="button"
                                        onClick={() =>
                                            navigate("/health/detail", { state: { reading: r, metric: key } })
                                        }
                                        className="rounded-2xl border border-gray-200 p-4 flex gap-4 items-center w-full text-left active:scale-[0.99] transition"
                                    >
                                        <div
                                            className="w-6 md:w-7 h-24 md:h-28 rounded-lg"
                                            style={{ backgroundColor: meta.color }}
                                        />
                                        <div className="flex-1 min-w-0 ml-auto text-right flex flex-col items-end space-y-2 md:space-y-3">
                                            <div
                                                className="text-3xl md:text-4xl font-semibold tracking-tight"
                                                style={{ color: meta.color }}
                                            >
                                                {meta.formatMain(r)}
                                            </div>
                                            <div className="text-gray-400 text-xs md:text-sm">
                                                {format(new Date(r.dt), "yyyy-MM-dd HH:mm:ss")}
                                            </div>
                                            {r.note ? (
                                                <div className="text-gray-400 text-xs md:text-sm truncate">{r.note}</div>
                                            ) : null}
                                        </div>
                                    </button>
                                ))}
                            </section>
                        )}


                        {/* 기록 없을 때: 추가 유도 카드(체중도 추가 가능) */}
                        {dayReadings.length === 0 && (
                            <section className="mt-4 space-y-3">
                                <button
                                    type="button"
                                    onClick={() => (meta.canCreate ? handleOpenAdd() : null)}
                                    className={`rounded-2xl border border-gray-200 p-4 flex gap-4 items-center w-full text-left active:scale-[0.99] transition ${meta.canCreate ? "" : "cursor-default"}`}
                                >
                                    <div className="w-6 md:w-7 h-24 md:h-28 rounded-lg bg-gray-300" />
                                    <div className="flex-1 min-w-0 ml-auto text-right flex flex-col items-end space-y-2 md:space-y-3">
                                        <div className="text-xl md:text-2xl font-semibold text-gray-400">
                                            {isSameDay(selected, new Date()) ? "오늘의 기록이 없습니다." : "이 날의 기록이 없습니다."}
                                        </div>
                                        <div className="text-gray-400 text-sm mt-1">
                                            {meta.canCreate ? "눌러서 기록 추가하기 »" : "기록 추가는 추후 지원 예정"}
                                        </div>
                                    </div>
                                </button>
                            </section>
                        )}
                    </div>
                </AuthCard>
            )}

            {/* 추가 모달(bp/weight) */}
            {meta.canCreate && (
                <HealthCareModal
                    open={openAdd}
                    mode="create"
                    variant={meta.modalVariant}
                    date={selected}
                    confirmLabel="변경 내용 저장"
                    busy={isPending}
                    onClose={handleCloseAdd}
                    onSubmit={handleSubmitAdd}
                />
            )}

            <BottomNav className="h-16" />
        </main>
    );
}
